import React, { useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { Sparkles, Flame, Gift, Share2, Copy, Award, Check, ArrowRight, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { themeClass } from "../components/ThemeChip";
import { PwaInstall } from "../components/PwaInstall";
import { InviteFriends } from "../components/InviteFriends";

const PLATFORMS = [
  { key: "instagram", label: "Instagram" },
  { key: "whatsapp", label: "WhatsApp" },
  { key: "facebook", label: "Facebook" },
  { key: "linkedin", label: "LinkedIn" },
];

function ShareSheet({ variants, onClose, themeTitle, weekTitle }) {
  const [active, setActive] = useState("instagram");
  const [text, setText] = useState(variants[active] || "");

  React.useEffect(() => { setText(variants[active] || ""); }, [active, variants]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Could not copy");
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try { await navigator.share({ text }); }
      catch { /* user cancelled */ }
    } else if (active === "whatsapp") {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`);
    } else if (active === "facebook") {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.origin)}&quote=${encodeURIComponent(text)}`);
    } else if (active === "linkedin") {
      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}&summary=${encodeURIComponent(text)}`);
    } else {
      copy();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4 fade-in"
         data-testid="share-sheet">
      <div className="card-elevated w-full max-w-lg p-6 sm:p-8 rise-in">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display text-2xl">Share what you learned</h3>
            <p className="text-xs text-muted-foreground">{themeTitle} · {weekTitle} · Text only, never photos.</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-muted" data-testid="share-close">
            <Check className="w-4 h-4"/>
          </button>
        </div>

        <div className="flex gap-2 mb-4 overflow-x-auto -mx-2 px-2">
          {PLATFORMS.map((p) => (
            <button key={p.key} onClick={() => setActive(p.key)}
                     className={`px-4 py-2 rounded-full border text-sm whitespace-nowrap ${
                       active === p.key ? "bg-[hsl(var(--neu-ink))] text-white border-transparent" : "bg-white/70"
                     }`}
                     data-testid={`share-tab-${p.key}`}>{p.label}</button>
          ))}
        </div>

        <textarea rows={6} value={text} onChange={(e) => setText(e.target.value)}
                   className="field font-body text-base" data-testid="share-textarea"/>

        <div className="flex gap-3 mt-4">
          <button onClick={copy} className="btn-ghost flex-1" data-testid="share-copy-btn">
            <Copy className="w-4 h-4"/> Copy
          </button>
          <button onClick={shareNative} className="btn-primary flex-1" data-testid="share-share-btn">
            <Share2 className="w-4 h-4"/> Share
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SessionComplete() {
  const loc = useLocation();
  const nav = useNavigate();
  const result = loc.state?.result;
  const theme = loc.state?.theme;
  const week = loc.state?.week;
  const [shareOpen, setShareOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  if (!result) return <Navigate to="/home" replace />;

  const alreadyCompleted = !!result.already_completed;
  const { points_earned = 0, streak = { current: 0, best: 0 },
          milestone, share, bonus = [] } = result;
  const colorClass = themeClass(theme?.color_token);

  return (
    <div className={`min-h-screen ${colorClass} relative`} data-testid="session-complete">
      <PwaInstall moment="post-session" />
      <div className="absolute inset-0 theme-surface opacity-40"/>
      <div className="relative max-w-md mx-auto px-6 pt-14 pb-10">

        <div className="text-center fade-in">
          <div className="mx-auto w-20 h-20 rounded-3xl bg-white/80 flex items-center justify-center mb-5 ring-soft">
            <Sparkles className="w-9 h-9 text-[hsl(var(--neu-orange))]"/>
          </div>
          <div className="text-xs uppercase tracking-widest theme-text mb-2">
            {alreadyCompleted ? "Already completed this week" : "Session complete"}
          </div>
          <h1 className="font-display text-5xl leading-tight">
            {alreadyCompleted ? "Already done. well done." : "Beautifully done."}
          </h1>
          <p className="text-muted-foreground mt-3">{theme?.title} · {week?.title}</p>
        </div>

        {milestone && (
          <div className="mt-7 card-elevated p-6 text-center rise-in bg-gradient-to-br from-white to-[hsl(var(--neu-orange))]/5 border-[hsl(var(--neu-orange))]/30"
               data-testid="milestone-celebration">
            <Award className="w-10 h-10 mx-auto text-[hsl(var(--neu-orange))]"/>
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--neu-orange))] mt-3 mb-1">Month complete</div>
            <h2 className="font-display text-3xl">{milestone.badge_title}</h2>
            <p className="text-sm text-muted-foreground mt-2">
              You finished all 4 weeks of <b>{milestone.theme_title}</b>.
            </p>
          </div>
        )}

        <div className="mt-7 grid grid-cols-2 gap-3 rise-in">
          <div className="card-soft p-5 text-center">
            <Gift className="w-5 h-5 mx-auto text-[hsl(var(--neu-pink))]"/>
            <div className="font-display text-3xl mt-2">{alreadyCompleted ? "-" : `+${points_earned}`}</div>
            <div className="text-xs text-muted-foreground">Points earned</div>
          </div>
          <div className="card-soft p-5 text-center">
            <Flame className="w-5 h-5 mx-auto text-[hsl(var(--neu-orange))]"/>
            <div className="font-display text-3xl mt-2">{streak.current} 🔥</div>
            <div className="text-xs text-muted-foreground">Weeks in a row</div>
          </div>
        </div>

        {bonus?.length > 0 && (
          <div className="mt-4 card-soft p-4 text-sm">
            🎁 Bonus: {bonus.map((b) => `+${b.points} for ${b.type.replace(/_/g, ' ')}`).join(", ")}
          </div>
        )}

        <div className="mt-7 space-y-3 rise-in">
          <button onClick={() => setShareOpen(true)} className="btn-primary w-full"
                  data-testid="complete-share-btn">
            <Share2 className="w-4 h-4"/> Share your insight
          </button>
          <button onClick={() => setInviteOpen(true)} className="btn-ghost w-full"
                  data-testid="complete-invite-btn">
            <UserPlus className="w-4 h-4"/> Invite a parent friend
          </button>
          <button onClick={() => nav("/wishes")} className="btn-ghost w-full"
                  data-testid="complete-wishes-btn">
            <Gift className="w-4 h-4"/> Check wish progress
          </button>
          <button onClick={() => nav("/home")} className="btn-ghost w-full"
                  data-testid="complete-home-btn">
            Back to home <ArrowRight className="w-4 h-4"/>
          </button>
        </div>
      </div>

      {shareOpen && share?.variants && (
        <ShareSheet variants={share.variants} onClose={() => setShareOpen(false)}
                     themeTitle={theme?.title} weekTitle={week?.title} />
      )}
      <InviteFriends open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  );
}
